import {
	RiAdminLine,
	RiBookOpenLine,
	RiDashboardLine,
	RiFileList3Line,
	RiNotification3Line,
	RiSearchLine,
	RiSettings3Line,
} from "@remixicon/react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface CommandPaletteProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const navItems = [
	{ to: "/dashboard", icon: RiDashboardLine, label: "Dashboard" },
	{ to: "/wiki", icon: RiBookOpenLine, label: "Wiki" },
	{ to: "/files", icon: RiFileList3Line, label: "Files" },
	{ to: "/notifications", icon: RiNotification3Line, label: "Notifications" },
	{ to: "/settings", icon: RiSettings3Line, label: "Settings" },
	{ to: "/admin", icon: RiAdminLine, label: "Admin Panel" },
] as const;

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
	const navigate = useNavigate();
	const inputRef = useRef<HTMLInputElement>(null);
	const [query, setQuery] = useState("");
	const [selectedIndex, setSelectedIndex] = useState(0);

	const filtered = navItems.filter((item) =>
		item.label.toLowerCase().includes(query.toLowerCase()),
	);

	useEffect(() => {
		if (open) {
			setQuery("");
			setSelectedIndex(0);
			requestAnimationFrame(() => inputRef.current?.focus());
		}
	}, [open]);

	useEffect(() => {
		setSelectedIndex(0);
	}, []);

	const handleSelect = useCallback(
		(to: string) => {
			onOpenChange(false);
			navigate({ to });
		},
		[navigate, onOpenChange],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "ArrowDown") {
				e.preventDefault();
				setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				setSelectedIndex((i) => Math.max(i - 1, 0));
			} else if (e.key === "Enter" && filtered[selectedIndex]) {
				e.preventDefault();
				handleSelect(filtered[selectedIndex].to);
			}
		},
		[filtered, selectedIndex, handleSelect],
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPortal>
				<DialogOverlay className="bg-black/60 supports-backdrop-filter:backdrop-blur-xs" />
				<DialogContent className="fixed top-[20%] left-1/2 z-50 w-full max-w-lg -translate-x-1/2 translate-y-0 border-0 bg-transparent p-0 shadow-none">
					<DialogTitle className="sr-only">Quick navigation</DialogTitle>
					<div className="rounded-xl border bg-background shadow-2xl ring-1 ring-foreground/10 overflow-hidden">
						<div className="flex items-center gap-3 border-b px-4 py-3">
							<RiSearchLine className="size-5 text-muted-foreground shrink-0" />
							<input
								ref={inputRef}
								type="text"
								placeholder="Search..."
								className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								onKeyDown={handleKeyDown}
							/>
							<kbd className="rounded border bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
								ESC
							</kbd>
						</div>
						<div className="max-h-[320px] overflow-y-auto p-2">
							{filtered.length === 0 ? (
								<div className="py-8 text-center text-xs text-muted-foreground">No results</div>
							) : (
								filtered.map((item, i) => (
									<button
										key={item.to}
										type="button"
										className={cn(
											"flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
											i === selectedIndex
												? "bg-accent text-accent-foreground"
												: "hover:bg-muted/70",
										)}
										onClick={() => handleSelect(item.to)}
										onMouseEnter={() => setSelectedIndex(i)}
									>
										<item.icon className="size-4 shrink-0 text-muted-foreground" />
										<span className="text-sm">{item.label}</span>
									</button>
								))
							)}
						</div>
						{filtered.length > 0 && (
							<div className="flex items-center gap-4 border-t px-4 py-2 text-[10px] text-muted-foreground">
								<span>
									<kbd className="font-mono">↑↓</kbd> navigate
								</span>
								<span>
									<kbd className="font-mono">↵</kbd> open
								</span>
								<span>
									<kbd className="font-mono">esc</kbd> close
								</span>
							</div>
						)}
					</div>
				</DialogContent>
			</DialogPortal>
		</Dialog>
	);
}
